import cn from 'classnames';
import { useRef, type FC } from 'react';
// might be used in next content update or removed once its moved to CMS
// import { useHomePageContext } from '../HomePageContext';
// import { TRADE } from 'dibs-buyer-layout/exports/userTypeEnums';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet';
import { trackEditorialImpressions, trackEditorialClick } from './helpers/EditorialTracking';

// components
import { Link } from 'dibs-elements/src/Link';
import { FormattedMessage } from 'dibs-react-intl';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

// styles
import dibsCss from 'dibs-css';
import styles from './HpSharedEditorialModule.module.css';

const consumerModules = [
    {
        image: 'https://a.1stdibscdn.com/dist/adhoc/homepage/consumer/hp_11-25-25/promo_1_trends.jpg',
        title: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModule.consumer.11-25-25.module1.title"
                defaultMessage="Our 2026 Design Predictions"
            />
        ),
        description: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModule.consumer.11-25-25.module1.description"
                defaultMessage="Discover a thoughtfully curated collection informed by current and emerging trends identified by our global community of interior designers."
            />
        ),
        cta: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModule.consumer.11-25-25.module1.cta"
                defaultMessage="Shop the Collection"
            />
        ),
        link: '/collections/designer-trends-2026/',
    },
    {
        image: 'https://a.1stdibscdn.com/dist/adhoc/homepage/consumer/hp_11-25-25/promo_2_jewelry.jpg',
        title: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModule.consumer.11-12-25.module2.title"
                defaultMessage="Gifts That Sparkle"
            />
        ),
        description: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModule.consumer.11-12-25.module2.description.dash"
                defaultMessage=" Give something extraordinary to someone exceptional. Explore iconic designs, top jewelry houses and more — and enjoy free shipping."
            />
        ),
        cta: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModule.consumer.11-12-25.module2.cta"
                defaultMessage="Shop Jewelry Gifts"
            />
        ),
        link: '/collections/holiday-gift-collection/jewelry/',
    },
    {
        image: 'https://a.1stdibscdn.com/dist/adhoc/homepage/consumer/hp_11-25-25/promo_3_hermes.jpg',
        title: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModule.consumer.11-12-25.module3.title"
                defaultMessage="Spotlight on Hermès"
            />
        ),
        description: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModule.consumer.11-12-25.module3.description"
                defaultMessage="Take home a piece from the renowned French label, whether you're shopping its coveted handbags or chic accessories."
            />
        ),
        cta: (
            <FormattedMessage
                id="homepage.HpSharedEditorialModule.consumer.11-12-25.module3.cta"
                defaultMessage="Shop Now"
            />
        ),
        link: '/creators/hermes/fashion/',
    },
];

export const HpSharedEditorialModule: FC<{ isMobile: boolean }> = ({ isMobile }) => {
    const editorialModuleRef = useRef(null);
    // might be used in next content update or removed once its moved to CMS
    // const { userType } = useHomePageContext();
    // const isTrade = userType === TRADE;
    const modules = consumerModules;
    const trackingItems = modules.map(module => ({ linkData: { path: module.link } }));

    const handleInitialDisplay = ({ inViewport }: { inViewport: boolean }): void => {
        if (trackingItems && inViewport) {
            trackEditorialImpressions(trackingItems);
        }
    };

    return (
        <>
            <VisibilityTracker
                onVisibilityChange={({ isVisible }) =>
                    handleInitialDisplay({ inViewport: isVisible })
                }
            />

            <div
                ref={editorialModuleRef}
                className={cn(dibsCss.pbLarger, {
                    [dibsCss.ptMedium]: isMobile,
                    [dibsCss.ptXlarge]: !isMobile,
                })}
            >
                <div
                    className={cn(dibsCss.textCenter, {
                        [dibsCss.mbSmall]: isMobile,
                        [dibsCss.mbLarge]: !isMobile,
                        [dibsCss.sassyFontHeaderLarge]: isMobile,
                        [dibsCss.sassyFontHeaderXLarge]: !isMobile,
                    })}
                >
                    <FormattedMessage
                        id="homepage.HpSharedEditorialModule.title"
                        defaultMessage="In the Spotlight"
                    />
                </div>
                <div
                    className={cn(dibsCss.flex, {
                        [styles.content]: !isMobile,
                        [dibsCss.flexCol]: isMobile,
                        [dibsCss.gapSmall]: isMobile,
                        [dibsCss.gapMedium]: !isMobile,
                    })}
                >
                    {modules.map((module, index) => (
                        <Link
                            key={module.link}
                            href={module.link}
                            className={cn(dibsCss.flex, dibsCss.flexCol, styles.text, {
                                [dibsCss.maxW33]: !isMobile,
                                [dibsCss.gapXsmall]: !isMobile,
                                [dibsCss.mbMedium]: isMobile,
                            })}
                            onClick={() =>
                                trackEditorialClick({ linkData: { path: module.link } }, index)
                            }
                        >
                            <img
                                alt=""
                                src={module.image}
                                srcSet={getSrcsetString(module.image)}
                                sizes="(max-width: 768px) 100vw, 33vw"
                                className={cn({
                                    [dibsCss.mbXsmall]: isMobile,
                                    [dibsCss.mbSmall]: !isMobile,
                                })}
                            />
                            <span
                                className={cn({
                                    [dibsCss.mbXsmall]: isMobile,
                                    [dibsCss.sassyFontHeaderLarge]: !isMobile,
                                    [dibsCss.sassyFontHeaderMedium]: isMobile,
                                })}
                            >
                                {module.title}
                            </span>
                            {!isMobile && (
                                <span className={dibsCss.sassyFontBodyMediumNormal}>
                                    {module.description}
                                </span>
                            )}
                            <span
                                className={cn(dibsCss.sassyFontBodyMediumNormal, styles.linkText)}
                            >
                                {module.cta}
                            </span>
                        </Link>
                    ))}
                </div>
            </div>
        </>
    );
};
